import { useEffect, useMemo, useState } from 'react';
import { Calculator } from 'lucide-react';
import { useLocale } from '../../contexts/LocaleContext';
import {
  calcMonthlyPayment,
  calcTotalInterest,
  DEFAULT_FINANCE,
  FINANCE_TERM_OPTIONS,
} from '../../utils/financeCalculator';
import '../AllCss/FinancingCalculator.css';

function formatUsd(value) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(value);
}

function FinancingCalculator({ vehiclePrice, onMonthlyChange }) {
  const { t } = useLocale();
  const price = Math.max(0, parseFloat(vehiclePrice) || 0);

  const [downPayment, setDownPayment] = useState(DEFAULT_FINANCE.downPayment);
  const [apr, setApr] = useState(DEFAULT_FINANCE.apr);
  const [termMonths, setTermMonths] = useState(DEFAULT_FINANCE.termMonths);

  const principal = Math.max(0, price - Math.min(downPayment, price));

  const monthlyPayment = useMemo(
    () => calcMonthlyPayment(principal, apr, termMonths),
    [principal, apr, termMonths]
  );

  const totalInterest = useMemo(
    () => calcTotalInterest(principal, monthlyPayment, termMonths),
    [principal, monthlyPayment, termMonths]
  );

  const totalPaid = principal + totalInterest;

  useEffect(() => {
    if (typeof onMonthlyChange === 'function') onMonthlyChange(monthlyPayment);
  }, [monthlyPayment, onMonthlyChange]);

  const label = (key, fallback) => {
    const v = t(`financing.${key}`);
    return v && !v.startsWith('financing.') ? v : fallback;
  };

  return (
    <div className="finance-calc" aria-labelledby="finance-calc-title">
      <div className="finance-calc-header">
        <Calculator size={20} aria-hidden />
        <h3 id="finance-calc-title">{label('calcTitle', 'Payment calculator')}</h3>
      </div>
      <p className="finance-calc-disclaimer">
        {label(
          'calcDisclaimer',
          'Estimates only. Actual rate and payment depend on credit approval and lender terms.'
        )}
      </p>

      <div className="finance-calc-grid">
        <label className="finance-calc-field">
          <span>{label('downPayment', 'Down payment')}</span>
          <input
            type="number"
            min={0}
            max={price}
            step={500}
            value={downPayment}
            onChange={(e) => setDownPayment(Math.max(0, Number(e.target.value) || 0))}
          />
        </label>
        <label className="finance-calc-field">
          <span>{label('apr', 'APR (%)')}</span>
          <input
            type="number"
            min={0}
            max={30}
            step={0.1}
            value={apr}
            onChange={(e) => setApr(Math.max(0, Number(e.target.value) || 0))}
          />
        </label>
        <label className="finance-calc-field">
          <span>{label('term', 'Loan term')}</span>
          <select value={termMonths} onChange={(e) => setTermMonths(Number(e.target.value))}>
            {FINANCE_TERM_OPTIONS.map((m) => (
              <option key={m} value={m}>
                {m} {label('months', 'months')}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="finance-calc-results">
        <div className="finance-calc-result-primary">
          <span className="finance-calc-result-label">{label('estimatedMonthly', 'Estimated monthly')}</span>
          <strong className="finance-calc-result-value">
            {formatUsd(monthlyPayment)}
            <span className="finance-calc-per-mo">/mo</span>
          </strong>
        </div>
        <div className="finance-calc-result-row">
          <span>{label('amountFinanced', 'Amount financed')}</span>
          <span>{formatUsd(principal)}</span>
        </div>
        <div className="finance-calc-result-row">
          <span>{label('totalInterest', 'Total interest')}</span>
          <span>{formatUsd(totalInterest)}</span>
        </div>
        <div className="finance-calc-result-row">
          <span>{label('totalPaid', 'Total paid over term')}</span>
          <span>{formatUsd(totalPaid)}</span>
        </div>
      </div>
    </div>
  );
}

export default FinancingCalculator;
